import {useMemo, useRef, useState} from "react";
import ajaxHandler from "@Ajax/v2/ajaxHandler";
import ReCAPTCHA from "react-google-recaptcha";
import MetadataEventModel from "@Events/models/MetadataEventModel";
import {useAppSelector} from "@Hooks/selector/useAppSelector";
import useAsyncEffect from "@Hooks/promises/useAsyncEffect";
import useSubmitBusRentFormEvent from "@Events/hooks/useSubmitBusRentFormEvent";

interface UseSubmitAttributes {
    setResponseStatus: (value: number) => void,
    fullName: string,
    email: string,
    country: string,
    phone: string,
    message: string,
}

export default function useSubmit(props: UseSubmitAttributes) {
    const {
        setResponseStatus,
        fullName,
        email,
        country,
        phone,
        message,
    } = props

    const [loading, setLoading] = useState(false)

    const [errors, setErrors] = useState([])
    const recaptchaRef = useRef<ReCAPTCHA>(null)

    const user = useAppSelector((state) => state.userProfile.user)
    const {
        isMobile,
        experiments,
        isSmallTablet,
        isTablet,
    } = useAppSelector((state) => state.page)

    const deviceType = useMemo(() => {
        if (isMobile) return "Mobile"
        if (isSmallTablet) return "Small Tablet"
        if (isTablet) return "Standard Tablet"
        return "Desktop"
    }, [])

    const windowResolution = `${window.visualViewport.width.toFixed(2)} x ${window.visualViewport.height.toFixed(2)}`
    const screenResolution = `${window.screen.availHeight} x ${window.screen.availWidth}`

    const busRentFormSubmitted = useSubmitBusRentFormEvent(email)

    const formParams = {
        fullName,
        email,
        phone,
        country,
        message,
        metadata: MetadataEventModel(experiments, user?.id),
        deviceType,
        windowResolution,
        screenResolution,
    }

    const post = async (token) => {
        setErrors([])
        setLoading(true)
        await busRentFormSubmitted()

        const response = await ajaxHandler({
            url: "/ajax/submit-rent-a-bus-form-data",
            type: "post",
            params: JSON.stringify({
                ...formParams,
                "g-recaptcha-response": token,
            }),
            handle: "ignore",
        })

        if (response?.data?.errors) {
            setErrors(response.data.errors)
        }
        setResponseStatus(response?.status)
    }

    const submit = async () => {
        if (loading) {
            await recaptchaRef?.current?.reset()
            const recaptcha = await recaptchaRef?.current?.executeAsync()

            if (recaptcha) {
                try {
                    await post(recaptcha)
                } catch (err) {
                    setErrors([err])
                } finally {
                    setLoading(false)
                }
            } else {
                setErrors(["Recaptcha verification required"])
                setLoading(false)
            }
        }
    }

    useAsyncEffect(submit, [loading])

    return {
        recaptchaRef,
        errors,
        setLoading,
        loading,
    }
}